import React, { useEffect } from "react";
import { SideDrawer, XIcon } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import withBrand from "@/components/with-brand";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import useWindowSize from "@/hooks/use-window-size/use-window-size";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";

import CoverDetailsDrawerContent from "./cover-details-drawer-content/cover-details-drawer-content";
import Styles from "./cover-details-drawer.module.css";

type DrawerDataObj = {
  benefitCopy: string;
  benefitSubtitle: string;
  benefitTitle: string;
  isExcludeUK?: boolean;
};

type CoverDetailsSideDrawerProps = {
  closeDrawerCta?: string;
  coverDetailsArea?: object | null;
  coverLevelTitle: string;
  drawerCopy: string;
  drawerData?: DrawerDataObj[];
  isOpen: boolean;
  setOpen: (value: boolean) => void;
  brand?: "staysure" | "avanti";
};

function CoverDetailsDrawer({
  coverLevelTitle,
  drawerCopy,
  drawerData,
  isOpen,
  setOpen,
  brand = "staysure",
  closeDrawerCta = "Close",
}: Readonly<CoverDetailsSideDrawerProps>) {
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const { t } = useWizardTranslation();

  useEffect(() => {
    setIsDrawerOpen(isOpen);
  }, [isOpen]);

  const windowSize = useWindowSize();
  const getDrawerWidth = () => {
    if (windowSize && windowSize >= 904) {
      return 504;
    }
    if (windowSize && windowSize >= 600) {
      return 376;
    }
    return 336;
  };
  return (
    <div
      data-testid="cover-details-drawer-wrapper-test-id"
      className={`${brand} ${Styles.coverDrawer}`}
    >
      <SideDrawer
        isOpen={isDrawerOpen}
        onCloseDrawer={() => {
          setIsDrawerOpen(false);
          setOpen(false);
        }}
        width={getDrawerWidth()}
        closeBtnWrapperStyles="grid justify-items-end md:mb-spacing-l sm:mb-spacing-m hidden"
        drawerContentWrapperClassName="md:p-spacing-xxl sm:p-spacing-xl"
        closeButton={<div />}
      >
        <div className="flex justify-end sm:mb-spacing-m lg:mb-spacing-l">
          <LabelButtonWrapper
            data-testid="cover-details-drawer-close-button"
            id="cover-details-drawer-close-button"
            type="button"
            hierarchy="tertiary"
            label={closeDrawerCta}
            onClick={() => {
              setIsDrawerOpen(false);
              setOpen(false);
            }}
            isRightIcon
            rightIcon={
              <XIcon
                stroke={selectedColorBrand["interactive-secondary-default"]}
              />
            }
            className={`text-[18px] font-body-font
          !px-spacing-xxs leading-[18px] h-[24px] text-border-accent-1-primary font-[600]  hover:text-surface-neutral-option-1 ${Styles.closeBtn} ${brand}`}
          />
        </div>
        <div className="font-header-font md:text-3xl sm:text-[29px] lg:text-3xl sm:leading-[33px] md:leading-[34px] text-text-title sm:w-[248px] md:w-[264px] lg:w-[392px]">
          {coverLevelTitle}
        </div>
        <div className="font-header-font md:text-3xl sm:text-[29px] lg:text-3xl sm:leading-[33px] md:leading-[34px] text-text-title mb-[8px] sm:w-[248px] md:w-[264px] lg:w-[392px] ">
          {t("what_is_included", "What’s included")}
        </div>

        <div className="font-proxima-nova font-normal text-xs leading-[27px] text-text-body sm:w-[248px] md:w-[264px] lg:w-[392px]">
          <RichText richText={drawerCopy} />
        </div>
        {drawerData?.map((data) => {
          const { benefitTitle, benefitSubtitle, benefitCopy } = data;
          return (
            <CoverDetailsDrawerContent
              key={benefitTitle}
              title={benefitTitle}
              subTitle={benefitSubtitle}
              benefitCopy={benefitCopy}
            />
          );
        })}
      </SideDrawer>
    </div>
  );
}

export default withBrand(CoverDetailsDrawer);
